import { useState, useEffect, useCallback, useRef } from "react";
import { addDays, format, parseISO, set } from "date-fns";
import { Alert, CloseAlert } from "../utils/Alert.js";
import { progressaoService } from "../Services/progressao.service.js";
import { formatDate, extractDate } from "../utils/dateUtils.js";
import { formatFileSize, normalizeString } from "../utils/fileUtils.js";
import api from "../Services/api.js";
import { useResetProjection } from "framer-motion";

const useProgressaoForm = (show, setShow, mode, progressaoEdit, verForm) => {
  const isEditMode = mode === "edit";

  // Shared States
  const [cursos, setCursos] = useState([]);
  const [selectedCursoNome, setSelectedCursoNome] = useState("");
  const [selectedCursoId, setSelectedCursoId] = useState("");
  const [turmas, setTurmas] = useState([]);
  const [selectedSemestreAno, setSelectedSemestreAno] = useState("");
  const [selectedTurmaNome, setSelectedTurmaNome] = useState("");
  const [selectedTurmaId, setSelectedTurmaId] = useState("");
  const [disciplinas, setDisciplinas] = useState([]);
  const [selectedDisciplinaNome, setSelectedDisciplinaNome] = useState("");
  const [selectedDisciplinaId, setSelectedDisciplinaId] = useState("");
  const [mostrarAlunosSelecionados, setMostrarAlunosSelecionados] = useState(true);
  const [mostrarAlunos, setMostrarAlunos] = useState(true);
  const [mostrarPdf, setMostrarPdf] = useState(false);
  const [alunos, setAlunos] = useState([]);
  const [alunosBk, setAlunosBk] = useState([]);
  const [selecionarAluno, setSelecionarAluno] = useState([]);
  const [INICIAL_ALUNOS_SELECIONADOS, setINICIAL_ALUNOS_SELECIONADOS] = useState([]);
  const [steps, setSteps] = useState(1);
  const [pdfProgTemp, setPdfProgTemp] = useState("");
  const [rascunho, setRascunho] = useState(false);
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileUrlEdit, setFileUrlEdit] = useState(null);
  const [removeuFile, setRemoveuFile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [loading3, setLoading3] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [dadosCarregados, setDadosCarregados] = useState(false);
  const [searchRm, setSearchRm] = useState("");
  const [searchNome, setSearchNome] = useState("");
  const [errorText, setErrorText] = useState("");
  const [fileSize, setFileSize] = useState({ bytes: null, kb: null, mb: null, gb: null });
  const [alunoLoading, setAlunoLoading] = useState({});
  const INICIAL_COMPLEMENTOS_PROG = {
    competencias: progressaoEdit?.competencias_habilidades || "",
    conhecimento: progressaoEdit?.conhecimento_conteudos || "",
    metodologia: progressaoEdit?.metodologia || "",
    criterios: progressaoEdit?.criterios_avaliacao || "",
    cronogramas: progressaoEdit?.cronogramas_etapas || "",
    material_apoio: progressaoEdit?.material_apoio || "",
    orientacoes: progressaoEdit?.orientacoes || "",
  };
  const [complementosProg, setComplementosProg] = useState(INICIAL_COMPLEMENTOS_PROG);

  const amanha = format(addDays(new Date(), 1), "yyyy-MM-dd");
  const depoisDeAmanha = format(addDays(new Date(), 2), "yyyy-MM-dd");

  const [dataInicio, setDataInicio] = useState(
    progressaoEdit?.data_inicio ? extractDate(progressaoEdit?.data_inicio) : ""
  );
  const [dataFim, setDataFim] = useState(progressaoEdit?.data_fim ? extractDate(progressaoEdit?.data_fim) : "");

  // Initial Effects
  useEffect(() => {
    if (!show) return;

    if (isEditMode && !verForm) {
      if (progressaoEdit?.pdf) {
        setLoading3(true);
        handleGetPdf(progressaoEdit?.pdf);
      }
    } else {
      setDataInicio(amanha);
      setDataFim(depoisDeAmanha);
      setErrorText("");
      handleListarCursos();
    }
  }, [show, isEditMode]);

  useEffect(() => {
    if (!show || !isEditMode || !progressaoEdit?.id) return;

    if (steps === 3 && !dadosCarregados && !verForm) {
      setDadosCarregados(true);
      setLoading(true);
      handleListarAlunosProgressoes(progressaoEdit.id);
      handleListarAlunos(progressaoEdit.curso_rh.split(", ")[0]);
    }
  }, [show, isEditMode, progressaoEdit?.id, steps]);

  // Filtered Students
  const filteredAlunos = alunos.filter((aluno) => {
    const searchRMNormalized = normalizeString(searchRm);
    const searchNomeNormalized = normalizeString(searchNome);
    const alunoRM = normalizeString(aluno.rm?.toString() || "");
    const alunoNome = normalizeString(aluno.nome_aluno || "");
    return alunoRM.includes(searchRMNormalized) && alunoNome.includes(searchNomeNormalized);
  });

  // API Calls
  const handleGetPdf = useCallback(async (pdfName) => {
    try {
      const { url, file, fileSize: data } = await progressaoService.getPdf(pdfName);
      setFile(file);
      setFileUrl(url);
      setFileSize({
        bytes: data.fileSizeInBytes,
        kb: data.fileSizeInKB,
        mb: data.fileSizeInMB,
        gb: data.fileSizeInGB,
      });
    } catch (error) {
      console.error("Erro ao fazer a requisição", error);
      Alert({ tipo: "Erro", mensagem: error.response?.data?.erro || "Erro ao visualizar o pdf!" });
    } finally {
      setLoading3(false);
    }
  }, []);

  const handleListarAlunosProgressoes = useCallback(async (id_progressao) => {
    try {
      const alunosValidos = await progressaoService.listarAlunosProgressoes(id_progressao);
      setSelecionarAluno(alunosValidos);
      setINICIAL_ALUNOS_SELECIONADOS(alunosValidos);
    } catch (err) {
      console.error(err);
      Alert({
        tipo: "Erro",
        mensagem: err.response?.data?.erro || "Erro interno de servidor",
      }).then((result) => {
        if (result.isConfirmed) setShow(false);
      });
    }
  }, []);

  const handleListarCursos = useCallback(async () => {
    if (verForm) return;
    try {
      const resultsCurso = await progressaoService.listarCursos();
      setCursos([{ label: "-", value: 0 }, ...resultsCurso]);
    } catch (err) {
      console.log("Erro: ", err);
      Alert({
        tipo: "Erro",
        mensagem: err.response?.data?.erro || "Erro interno de servidor",
      }).then((result) => {
        if (result.isConfirmed) setShow(false);
      });
    }
  }, []);

  const handleListarTurmas = useCallback(async (cursoId) => {
    setAlunos([]);
    setTurmas([]);
    setDisciplinas([]);
    setSelecionarAluno([]);
    setSelectedDisciplinaId(0);
    setErrorText("");
    if (cursoId == 0 || cursoId == "") {
      setSelectedTurmaId(0);
    } else if (cursoId != 0 || cursoId != undefined || cursoId != "") {
      try {
        const resultstTurmas = await progressaoService.listarTurmas(cursoId);
        setTurmas([{ label: "-", value: 0 }, ...resultstTurmas]);
      } catch (err) {
        if (err.response?.data?.erro === "Não possui Turmas.") {
          setDisciplinas([]);
          setSelectedDisciplinaId(0);
          setTurmas([{ label: "Esse curso não possui turmas associadas", value: 0 }]);
        }
        console.log("Erro: ", err);
      }
    }
  }, []);

  const handleGetDisciplina = useCallback(async (turmaId) => {
    setAlunos([]);
    setDisciplinas([]);
    setSelecionarAluno([]);
    setSelectedDisciplinaId(0);
    if (turmaId == 0 || turmaId == "") {
      setSelectedDisciplinaId(0);
    } else if (turmaId != 0 || turmaId != undefined || turmaId != "") {
      try {
        const resultsDisciplina = await progressaoService.getDisciplina(turmaId);
        setDisciplinas([{ label: "-", value: 0 }, ...resultsDisciplina]);
      } catch (err) {
        if (err.response?.data?.erro === "Não possui disciplinas.") {
          setDisciplinas([{ label: "Essa turma não possui disciplinas associadas", value: 0 }]);
        }
        console.log("Erro: ", err);
      }
    }
  }, []);

  const handleListarAlunos = useCallback(
    async (cursoId) => {
      const cursoSelecionado = cursoId || selectedCursoId;
      const disciplinaValida = isEditMode || selectedDisciplinaId > 0;

      if (cursoSelecionado && disciplinaValida) {
        setLoading(true);
        try {
          const data = await progressaoService.listarAlunos(cursoSelecionado);
          if (data && data.length !== 0) {
            setAlunos(data);
            setAlunosBk(data);
          } else {
            setErrorText("Nenhum aluno encontrado!");
            setAlunosBk([]);
            setAlunos([]);
          }
        } catch (err) {
          console.error(err);
          setErrorText("Nenhum aluno encontrado!");
          setAlunosBk([]);
          setAlunos([]);
        } finally {
          setLoading(false);
          setLoading2(false);
        }
      } else {
        setAlunosBk([]);
        setAlunos([]);
      }
    },
    [isEditMode, selectedCursoId, selectedDisciplinaId]
  );

  const handleEnviarAlunoParaBackend = useCallback(
    async (aluno, acao) => {
      const data = {
        id_progressao: progressaoEdit.id,
        id_aluno: aluno.id,
        rm_aluno: aluno.rm,
        ativo: aluno.ativo,
        dt_termino: aluno.dt_termino,
        acao: acao,
      };
      return await progressaoService.enviarAlunoParaBackend(data);
    },
    [progressaoEdit?.id]
  );

  // Student Management
  const handleListarAlunos2 = useCallback(() => {
    if (!alunosBk.length) return;
    const alunosFiltrados = alunosBk.filter(
      (aluno) => !selecionarAluno.some((selected) => selected.rm === aluno.rm || selected.id === aluno.id)
    );
    if (JSON.stringify(alunosFiltrados) !== JSON.stringify(alunos)) {
      setAlunos(alunosFiltrados);
    }
  }, [selecionarAluno, alunosBk, alunos]);

  useEffect(() => {
    if (!show || !alunosBk.length) return;
    handleListarAlunos2();
  }, [show, mostrarAlunos, selecionarAluno, alunosBk, handleListarAlunos2]);

  useEffect(() => {
    if (!show && isEditMode && !selectedCursoId) return;
    setErrorText("");
    handleListarTurmas(selectedCursoId);
  }, [show, selectedCursoId, handleListarTurmas, isEditMode]);

  useEffect(() => {
    if (!show || !selectedTurmaId || selectedTurmaId === "0") return;
    setErrorText("");
    handleGetDisciplina(selectedTurmaId);
  }, [show, selectedTurmaId, handleGetDisciplina]);

  useEffect(() => {
    if (!show && isEditMode && !selectedCursoId && !selectedTurmaId) return;
    setErrorText("");
    setAlunos([]);
  }, [selectedDisciplinaId, isEditMode, show, selectedCursoId, selectedTurmaId]);

  useEffect(() => {
    if (!show || !selectedCursoId || !selectedDisciplinaId || selectedCursoId === "0" || selectedDisciplinaId === "0")
      return;
    setErrorText("");
    handleListarAlunos();
  }, [show, selectedCursoId, selectedDisciplinaId, handleListarAlunos]);

  const handleSelecionarAluno = useCallback(
    async (aluno) => {
      setSearchNome("");
      setSearchRm("");

      // if (!isEditMode) {
      setSelecionarAluno((prevState) =>
        prevState.some((selected) => selected.id === aluno.id && selected.rm === aluno.rm)
          ? prevState.filter((selected) => selected.id !== aluno.id || selected.rm !== aluno.rm)
          : [...prevState, aluno]
      );
      return;
      // }

      // const jaEstaSeleccionado = selecionarAluno.some(
      //   (selected) => selected.id === aluno.id && selected.rm === aluno.rm
      // );
      // const acao = jaEstaSeleccionado ? "deletar" : "inserir";

      // if (selecionarAluno.length === 1 && acao !== "inserir") {
      //   return Alert({ tipo: "Alert-popup", mensagem: `Selecione pelo menos um aluno!`, posicao: "top-right" });
      // }

      // setAlunoLoading((prev) => ({ ...prev, [aluno.id]: true }));

      // try {
      //   const response = await handleEnviarAlunoParaBackend(aluno, acao);
      //   if (response.status !== 200) {
      //     console.error("Erro ao inserir aluno na progressão", response);
      //     Alert({
      //       tipo: "Erro-popup",
      //       mensagem: `Erro ao ${acao === "inserir" ? "adicionar" : "remover"} o aluno. Por favor, tente novamente.`,
      //       posicao: "top-right",
      //     });
      //     return;
      //   }
      //   setSelecionarAluno((prevState) =>
      //     jaEstaSeleccionado
      //       ? prevState.filter((selected) => selected.id !== aluno.id || selected.rm !== aluno.rm)
      //       : [...prevState, aluno]
      //   );
      // } catch (error) {
      //   Alert({
      //     tipo: "Erro-popup",
      //     mensagem: `Erro ao ${acao === "inserir" ? "adicionar" : "remover"} o aluno. Por favor, tente novamente.`,
      //     posicao: "top-right",
      //   });
      // } finally {
      //   setAlunoLoading((prev) => ({ ...prev, [aluno.id]: false }));
      // }
    },
    [selecionarAluno, handleEnviarAlunoParaBackend, isEditMode]
  );

  // File Handling
  const handleFileChange = useCallback(
    (e) => {
      const selectedFile = e.target.files[0];
      if (selectedFile && selectedFile.type === "application/pdf") {
        // Verificar tamanho do arquivo (limite de 5MB para uma margem de segurança)
        const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
        if (selectedFile.size > maxSizeInBytes) {
          Alert({
            tipo: "Alert",
            mensagem: `O arquivo selecionado é muito grande (${formatFileSize(
              selectedFile.size
            )}/5MB).`,
          });
          return;
        } else {
          setFile(selectedFile);
        }

        // Calcular tamanho do arquivo
        setFileSize({
          bytes: selectedFile.size,
          kb: (selectedFile.size / 1024).toFixed(2),
          mb: (selectedFile.size / (1024 * 1024)).toFixed(2),
          gb: (selectedFile.size / (1024 * 1024 * 1024)).toFixed(2),
        });

        // Usar URL.createObjectURL para melhor performance com arquivos grandes
        const fileUrl = URL.createObjectURL(selectedFile);
        if (isEditMode) {
          setFileUrlEdit(fileUrl);
        } else {
          setFileUrl(fileUrl);
        }
      } else {
        selectedFile && Alert({ tipo: "Erro2", mensagem: "Apenas arquivos PDF são permitidos." });
        setFile(null);
      }
    },
    [isEditMode]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragActive(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile && droppedFile.type !== "application/pdf") {
        Alert({ tipo: "Alert3", mensagem: "Somente arquivos PDF são aceitos." });
        setDragActive(false);
        return false;
      } else if (droppedFile) {
        // Verificar tamanho do arquivo (limite de 5MB para uma margem de segurança)
        const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
        if (droppedFile.size > maxSizeInBytes) {
          Alert({
            tipo: "Alert",
            mensagem: `O arquivo é muito grande (${formatFileSize(
              droppedFile.size
            )}/5MB).`,
          });
          return;
        } else {
          setFile(droppedFile);
        }

        // Calcular tamanho do arquivo
        setFileSize({
          bytes: droppedFile.size,
          kb: (droppedFile.size / 1024).toFixed(2),
          mb: (droppedFile.size / (1024 * 1024)).toFixed(2),
          gb: (droppedFile.size / (1024 * 1024 * 1024)).toFixed(2),
        });

        // Usar URL.createObjectURL para melhor performance
        if (isEditMode) setFileUrlEdit(URL.createObjectURL(droppedFile));
        else setFileUrl(URL.createObjectURL(droppedFile));
      }
      setDragActive(false);
    },
    [isEditMode]
  );

  // Date Handling
  const handleDataInicioChange = (e) => {
    const newDataInicio = e.target.value;
    setDataInicio(newDataInicio);
    if (new Date(newDataInicio) >= new Date(dataFim)) {
      const newDataFim = format(addDays(parseISO(newDataInicio), 1), "yyyy-MM-dd");
      setDataFim(newDataFim);
    }
  };

  const handleDataFimChange = (e) => {
    const newDataFim = e.target.value;
    if (dataInicio && newDataFim >= dataInicio) {
      setDataFim(newDataFim);
    } else {
      setDataFim(dataInicio);
    }
  };

  const verificaComplemento = () => {
    const { orientacoes, material_apoio, ...objComplementosProg } = complementosProg;
    return (todosPreenchidos = Object.values(objComplementosProg).every((valor) => {
      const valorLimpo = valor?.toString().trim();
      return valorLimpo && valorLimpo.length > 0;
    }));
  };

  const isEqual = (obj1, obj2) => {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every((key) => obj1[key] === obj2[key]);
  };

  const pdfAlterado = (removido = false) => {
    const temPdfOriginal = !!progressaoEdit?.pdf;
    const temNovoArquivo = !!file;
    const removeuPdf = temPdfOriginal && !fileUrl && !fileUrlEdit && !temNovoArquivo;
    const adicionouPdf = !temPdfOriginal && (temNovoArquivo || fileUrlEdit);
    const substituiuPdf = temPdfOriginal && temNovoArquivo && !fileUrl && fileUrlEdit;

    if (!!removido) {
      return removeuPdf;
    }

    return removeuPdf || adicionouPdf || substituiuPdf;
  };

  const hasChanges = (dataInicioAtual, dataFimAtual, complementosProgAtual, alunosAtuais) => {
    // Verificar se progressaoEdit existe
    if (isEditMode && !progressaoEdit) return true;
    // Comparar datas
    const datasAlteradas = isEditMode
      ? format(parseISO(dataInicioAtual), "yyyy-MM-dd") !==
          format(parseISO(progressaoEdit.data_inicio), "yyyy-MM-dd") ||
        format(parseISO(dataFimAtual), "yyyy-MM-dd") !== format(parseISO(progressaoEdit.data_fim), "yyyy-MM-dd")
      : format(parseISO(dataInicioAtual), "yyyy-MM-dd") !== amanha ||
        format(parseISO(dataFimAtual), "yyyy-MM-dd") !== depoisDeAmanha;

    // Comparar complementos
    const complementosAlterado = !isEqual(complementosProgAtual, INICIAL_COMPLEMENTOS_PROG);
    // Comparar alunos (com tratamento para arrays vazios/undefined)
    const alunosAlterados = JSON.stringify(INICIAL_ALUNOS_SELECIONADOS || []) !== JSON.stringify(alunosAtuais || []);

    // Verificar mudanças no PDF

    return datasAlteradas || complementosAlterado || alunosAlterados || pdfAlterado();
  };

  const DeleteProgreTemp = async () => {
    try {
      await api
        .delete("/progressao-temp/deletar-progressao")
        .then(async (response) => {
          // console.log(response);
        })
        .catch((erro) => {
          console.log(erro);
        });
    } catch (err) {
      console.log("Erro: ", err);
      setDeletandoId(null);
    }
  };

  const handleUploadClick = async () => {
    if (selectedCursoId && selectedTurmaId && selectedDisciplinaId && dataInicio && dataFim && verificaComplemento) {
      const agora = new Date();
      const [dataStr, horaStr] = agora.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }).split(" ");
      const [dia, mes, ano] = dataStr.split("/");
      const [horas, minutos, segundos] = horaStr.split(":");
      const data = {
        curso: `${selectedCursoId}, ${selectedCursoNome}`,
        turma: `${selectedTurmaId}, ${selectedTurmaNome}`,
        disciplina: `${selectedDisciplinaId}, ${selectedDisciplinaNome}`,
        data_inicio: dataInicio,
        data_fim: dataFim,
        ...(file || fileUrl ? { id_pdf: `${ano}${mes}${dia}${horas}${minutos}${segundos}`.replace(",", "") } : {}),
      };

      if (data.data_inicio === data.data_fim) {
        return Alert({
          tipo: "Erro",
          mensagem: `<p>Insira uma <strong>Data de Início</strong> diferente da <strong>Data de Limite da Entrega</strong></p>`,
        });
      }

      if (data.data_inicio > data.data_fim) {
        return Alert({
          tipo: "Erro",
          mensagem: `<p>Insira uma <strong>Data de Limite da Entrega</strong> maior que <strong>Data de Início</strong></p>`,
        });
      }

      const hoje = new Date().toISOString().split("T")[0];
      if (data.data_inicio < hoje) {
        return Alert({
          tipo: "Erro",
          mensagem: `<p>Insira uma <strong>Data de Início</strong> maior que <strong>${hoje}</strong></p>`,
        });
      }
      const { isConfirmed } = await Alert({
        tipo: "Alert",
        mensagem: "Os alunos selecionados receberão e-mail com os dados da progressão!",
      });
      if (isConfirmed) {
        try {
          setUploading(true);
          Alert({
            icon: "",
            tipo: "Loading",
            titulo: "Aguarde...",
            mensagem: "Criando a progressão parcial. Por favor, aguarde!",
          });
          const formData = new FormData();
          Object.keys(data).forEach((key) => {
            formData.append(key, data[key]);
          });
          formData.append("complementosProg", JSON.stringify(complementosProg));
          if (file && fileUrl && !pdfProgTemp) {
            formData.append("file", file);
          } else if (pdfProgTemp) {
            formData.append("pdf_name", pdfProgTemp);
          }

          if (rascunho) {
            formData.append("rascunho", true);
          }

          formData.append("alunos", JSON.stringify(selecionarAluno));

          const response = await progressaoService.cadastrarProgressao(formData);

          //fazer o método de exclusao da progressao temporaria aqui
          // if(response.ok){
          //   const responseTemp = await
          // }
          CloseAlert();

          if (response.status !== 200) {
            setUploading(false);

            return await Alert({
              tipo: "Erro",
              mensagem: response.data.mensagem,
            });
          }

          if (response.data.emailsFalharam && response.data.emailsFalharam > 0) {
            let mensagemEmail = `Progressão criada com sucesso, mas houve falhas no envio de ${response.data.emailsFalharam} email(s).`;

            if (response.data.detalhesEmailsFalharam && response.data.detalhesEmailsFalharam.length > 0) {
              const emailsComErro = response.data.detalhesEmailsFalharam
                .map((erro) => `RM: ${erro.rm} (${erro.email})`)
                .join("<br>");
              mensagemEmail += `<br><br><strong>Emails com erros:</strong><br>${emailsComErro}`;
            }

            await Alert({
              tipo: "Alert",
              mensagem: mensagemEmail,
            });
          } else {
            await Alert({
              tipo: "Sucesso2",
              mensagem: response.data.mensagem,
            });
            // try{
            DeleteProgreTemp();
            // }
          }

          setUploading(false);
          setShow(false);
        } catch (error) {
          CloseAlert();
          console.error("Erro ao enviar o arquivo:", error);
          setUploading(false);
          await Alert({ tipo: "Erro", mensagem: "Ocorreu um erro ao adicionar a progressão. Tente novamente." });
        }
      }
    } else {
      Alert({ tipo: "Alert3", mensagem: "Há campos a serem preenchidos." });
    }
  };

  const handleEditSave = useCallback(async () => {
    if (!hasChanges(dataInicio, dataFim, complementosProg, selecionarAluno)) {
      await Alert({ tipo: "Alert3", mensagem: "Nenhuma alteração foi detectada. Não há necessidade de salvar." });
      return;
    }

    const result = pdfAlterado(true)
      ? await Alert({
          tipo: "Alert",
          mensagem: "Deseja salvar a progressão <b>sem PDF</b>?",
        })
      : { isConfirmed: true };

    if (result.isConfirmed) {
      const Inicio = dataInicio || format(parseISO(progressaoEdit?.data_inicio), "yyyy-MM-dd");
      const Fim = dataFim || format(parseISO(progressaoEdit?.data_fim), "yyyy-MM-dd");

      const agora = new Date();
      const [dataStr, horaStr] = agora.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }).split(" ");
      const [dia, mes, ano] = dataStr.split("/");
      const [horas, minutos, segundos] = horaStr.split(":");

      const data = {
        id_progressao: progressaoEdit.id,
        disciplina: progressaoEdit.disciplina_rh.split(",")[1],
        professor: progressaoEdit.professor,
        data_inicio: Inicio,
        data_fim: Fim,
        id_pdf: progressaoEdit?.pdf
          ? progressaoEdit?.pdf
          : file
          ? `${ano}${mes}${dia}${horas}${minutos}${segundos}`.replace(",", "")
          : "",
        ...(file ? {} : { sem_pdf: true }),
      };

      const dataInicioObj = new Date(data.data_inicio).toISOString().split("T")[0];
      const dataFimObj = new Date(data.data_fim).toISOString().split("T")[0];

      if (dataInicioObj > dataFimObj || dataInicioObj === dataFimObj) {
        return Alert({
          tipo: "Erro",
          mensagem: `<p>Insira uma <strong>Data de Limite da Entrega</strong> maior que <strong>Data de Início</strong></p>`,
        });
      }

      if (!verificaComplemento) {
        return Alert({ tipo: "Alert3", mensagem: "Há campos no formulário serem preenchidos." });
      }

      try {
        Alert({
          tipo: "Loading",
          titulo: "Salvando...",
          mensagem: `Salvando as alterações!`,
        });

        setUploading(true);
        const formData = new FormData();
        Object.keys(data).forEach((key) => {
          formData.append(key, data[key]);
        });
        formData.append("complementosProg", JSON.stringify(complementosProg));
        formData.append("alunos", JSON.stringify(selecionarAluno));
        if (fileUrlEdit || file) {
          formData.append("file", file);
        }

        const res = await progressaoService.editarProgressao(formData);

        CloseAlert();
        if (res.status !== 200) {
          setUploading(false);
          console.error("Erro ao editar progressão", res);
          return await Alert({ tipo: "Erro", mensagem: "Ocorreu um erro ao editar a progressão" });
        }

        if (res.data.emailsFalharam && res.data.emailsFalharam > 0) {
          let mensagemEmail = `Progressão editada com sucesso, mas houve falhas no envio de ${res.data.emailsFalharam} email(s) para novos alunos adicionados.`;

          if (res.data.detalhesEmailsFalharam && res.data.detalhesEmailsFalharam.length > 0) {
            const emailsComErro = res.data.detalhesEmailsFalharam
              .map((erro) => `RM: ${erro.rm} (${erro.email})`)
              .join("<br>");
            mensagemEmail += `<br><br><strong>Emails com erros:</strong><br>${emailsComErro}`;
          }

          await Alert({
            tipo: "Alert",
            mensagem: mensagemEmail,
          });
        } else {
          await Alert({
            tipo: "Sucesso2",
            mensagem: "As alterações foram salvas com sucesso!",
          });
        }

        handleFechar(null, true);
      } catch (error) {
        console.error("Erro ao salvar as alterações:", error);
        setUploading(false);
        await Alert({ tipo: "Erro", mensagem: "Ocorreu um erro ao salvar as alterações. Tente novamente mais tarde!" });
      }
    }
  }, [dataInicio, dataFim, progressaoEdit, file, fileUrl, fileUrlEdit, selecionarAluno, complementosProg]);

  const handleFechar = useCallback(
    async (e, salvar = false) => {
      const { isConfirmed } =
        !salvar && hasChanges(dataInicio, dataFim, complementosProg, selecionarAluno)
          ? await Alert({
              tipo: "Alert",
              mensagem: "Há alterações. Deseja realmente descartá-las?",
              titulo: "Descartar alterações?",
            })
          : { isConfirmed: true };

      if (!isConfirmed) return;

      if (!salvar && (uploading || loading || loading2 || loading3 || Object.values(alunoLoading).some(Boolean))) {
        Alert({ tipo: "Alert-popup", mensagem: "Aguarde! Salvando as alterações..." });
        return;
      }
      e && e?.preventDefault();

      // Limpar URLs de objeto da memória antes de resetar os estados
      if (fileUrl && fileUrl.startsWith("blob:")) {
        URL.revokeObjectURL(fileUrl);
      }
      if (fileUrlEdit && fileUrlEdit.startsWith("blob:")) {
        URL.revokeObjectURL(fileUrlEdit);
      }

      setAlunos([]);
      setSelecionarAluno([]);
      setDataInicio("");
      setDataFim("");
      setFile(null);
      setFileUrl(null);
      setFileUrlEdit(null);
      setShow(false);
      setComplementosProg({
        competencias: "",
        conhecimento: "",
        metodologia: "",
        criterios: "",
        cronogramas: "",
        material_apoio: "",
        orientacoes: "",
      });
      setSteps(1);
      setSearchRm("");
      setSearchNome("");
      setLoading(false);
      setLoading2(false);
      setLoading3(false);
      setAlunoLoading({});
    },
    [
      isEditMode,
      file,
      fileUrl,
      fileUrlEdit,
      dataInicio,
      dataFim,
      complementosProg,
      selecionarAluno,
      uploading,
      loading,
      loading2,
      loading3,
      alunoLoading,
    ]
  );

  const hoje = formatDate(new Date());
  const inicio = new Date(progressaoEdit?.data_inicio);
  const naoComecou = new Date() <= inicio;

  const determinarDataInicio = () => {
    if (!isEditMode) {
      return dataInicio || amanha;
    }
    const dataInicioProgressao = extractDate(progressaoEdit?.data_inicio);
    if (naoComecou) {
      const data_inicio = dataInicio || dataInicioProgressao;
      if (data_inicio >= dataFim) {
        setDataFim(formatDate(addDays(parseISO(data_inicio), 1)));
      }
      return data_inicio;
    }
    return dataInicioProgressao;
  };

  const determinarDataFim = () => {
    if (!isEditMode) {
      return dataFim || dataInicio || depoisDeAmanha;
    }
    const dataFimProgressao = dataFim == "" && extractDate(progressaoEdit?.data_fim);
    return dataFim || dataFimProgressao;
  };

  const determinarMinimoDataFim = () => {
    if (!isEditMode) {
      return dataInicio ? formatDate(addDays(parseISO(dataInicio), 1)) : depoisDeAmanha;
    }
    const dataBase = dataInicio || extractDate(progressaoEdit?.data_inicio);
    if (!dataBase) return depoisDeAmanha;
    const dataBaseAcrescida = formatDate(addDays(parseISO(dataBase), 1));
    const data_inicio = dataInicio;
    return data_inicio > determinarDataFim()
      ? setDataFim(formatDate(addDays(parseISO(data_inicio), 1)))
      : dataBaseAcrescida < hoje
      ? formatDate(addDays(parseISO(hoje), 1))
      : dataBaseAcrescida;
  };

  const valueInputDateInicio = determinarDataInicio();
  const valueInputDateFim = determinarDataFim();
  const minimoDateFim = determinarMinimoDataFim();

  const handleContinuar = () => {
    if (steps === 1) {
      const { orientacoes, material_apoio, ...objComplementosProg } = complementosProg;
      const todosPreenchidos = Object.values(objComplementosProg).every((valor) => {
        const valorLimpo = valor?.toString().trim();
        return valorLimpo && valorLimpo.length > 0;
      });

      if (todosPreenchidos) {
        setSteps(2);
      } else {
        Alert({
          tipo: "Alert-popup",
          mensagem: "Preencha todos os campos",
        });
      }
    } else if (steps === 2) {
      // if (!fileUrl && !fileUrlEdit) {
      //   return Alert({ tipo: "Alert3", mensagem: "Insira um pdf!" });
      // }
      if (dataInicio === dataFim) {
        return Alert({
          tipo: "Erro",
          mensagem: `<p>Insira uma <strong>Data de Início</strong> diferente da <strong>Data de Limite da Entrega</strong></p>`,
        });
      }
      if (dataInicio > dataFim) {
        return Alert({
          tipo: "Erro",
          mensagem: `<p>Insira uma <strong>Data de Limite da Entrega</strong> maior que <strong>Data de Início</strong></p>`,
        });
      }
      const hoje = new Date().toISOString().split("T")[0];
      if (!isEditMode && dataInicio < hoje) {
        return Alert({
          tipo: "Erro",
          mensagem: `<p>Insira uma <strong>Data de Início</strong> maior que <strong>${hoje}</strong></p>`,
        });
      }
      setSteps(3);
    } else if (steps === 3) {
      if (selecionarAluno.length == 0) {
        return Alert({ tipo: "Alert3", mensagem: "Selecione um ou mais alunos" });
      }
      setSteps(4);
    }
  };

  const semAlunos =
    !isEditMode &&
    (!alunos.length || (selectedDisciplinaId == 0 && selectedDisciplinaId == "" && !selectedDisciplinaId));

  //progressão temporária

  const lastSavedDataRef = useRef(null);
  const [savedDados, setSavedDados] = useState({});
  const fetchProgressaoTemporaria = useCallback(async () => {
    if (!isEditMode) {
      try {
        const response = await api.get("/progressao-temp/buscar-progressoes");

        if (response.status !== 200) {
          console.log("Nenhum dado salvo encontrado");
          return;
        }

        // console.log(response);
        const savedData = response.data;
        setSavedDados(savedData);
        if (savedData && Object.keys(savedData).length > 0) {
          setComplementosProg({
            competencias: savedData?.competencias_habilidades || "",
            conhecimento: savedData?.conhecimento_conteudos || "",
            metodologia: savedData?.metodologia || "",
            criterios: savedData?.criterios_avaliacao || "",
            cronogramas: savedData?.cronogramas_etapas || "",
            material_apoio: savedData?.material_apoio || "",
            orientacoes: savedData?.orientacoes || "",
          });
          // console.log("savedData?.data_inicio -> ", savedData?.data_inicio);
          // console.log("savedData?.data_fim ->", savedData?.data_fim);
          setDataInicio(savedData?.data_inicio);
          setDataFim(savedData?.data_fim);

          lastSavedDataRef.current = savedData;

          if (savedData?.pdf) {
            setLoading3(true);
            await handleGetPdf(savedData.pdf);
            setPdfProgTemp(savedData.pdf);
          }

          setRascunho(true);
          console.log("Dados recuperados com sucesso");
        }
      } catch (error) {
        // console.error("Erro ao buscar dados salvos:", error);
      }
    }
  }, [setComplementosProg]);

  const saveProgressaoTemporaria = useCallback(async () => {
    if (!isEditMode) {
      try {
        const agora = new Date();
        const [dataStr, horaStr] = agora.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }).split(" ");
        const [dia, mes, ano] = dataStr.split("/");
        const [horas, minutos, segundos] = horaStr.split(":");

        const idPdfNovo = `${ano}${mes}${dia}${horas}${minutos}${segundos}`.replace(",", "");
        const idPdfExistente = savedDados.pdf;

        // Remover extensões .pdf duplicadas do nome do arquivo
        const fileName = file?.name ? file.name.replace(/\.pdf(\.pdf)*$/i, ".pdf") : null;
        const savedFileName = idPdfExistente ? idPdfExistente.replace(/\.pdf(\.pdf)*$/i, ".pdf") : null;

        const data = {
          competencias_habilidades: complementosProg.competencias,
          conhecimento_conteudos: complementosProg.conhecimento,
          metodologia: complementosProg.metodologia,
          criterios_avaliacao: complementosProg.criterios,
          cronogramas_etapas: complementosProg.cronogramas,
          material_apoio: complementosProg.material_apoio,
          orientacoes: complementosProg.orientacoes,
          data_inicio: dataInicio,
          data_fim: dataFim,
          id_pdf: file && fileUrl && savedFileName !== fileName ? idPdfNovo : idPdfExistente,
        };

        let response;

        // Se o id_pdf já existe, envia como JSON normalmente
        if (data.id_pdf && (!file || savedFileName === fileName)) {
          if (!lastSavedDataRef?.current) {
            response = await api.post("/progressao-temp/cadastrar-progressao", data);
          } else {
            response = await api.put("/progressao-temp/editar-progressao", data);
          }
        } else {
          // Caso contrário, envia como FormData (com arquivo)
          const formData = new FormData();
          Object.keys(data).forEach((key) => {
            formData.append(key, data[key]);
          });
          if (file && fileUrl) formData.append("file", file);

          if (!lastSavedDataRef?.current) {
            response = await api.post("/progressao-temp/cadastrar-progressao", formData);
          } else {
            response = await api.put("/progressao-temp/editar-progressao", formData);
          }
        }

        if (response.status === 200) {
          lastSavedDataRef.current = data;
          console.log("Dados salvos automaticamente");
        } else {
          console.error("Erro ao salvar dados:", response.statusText);
        }
      } catch (error) {
        console.error("Erro na requisição de salvamento:", error);
      }
    }
  }, [complementosProg, dataInicio, dataFim, file, fileUrl, savedDados.pdf]);

  useEffect(() => {
    fetchProgressaoTemporaria();
  }, [fetchProgressaoTemporaria]);

  // Cleanup effect para liberar URLs de objeto da memória
  useEffect(() => {
    return () => {
      // Limpar URLs de objeto quando o componente for desmontado
      if (fileUrl && fileUrl.startsWith("blob:")) {
        URL.revokeObjectURL(fileUrl);
      }
      if (fileUrlEdit && fileUrlEdit.startsWith("blob:")) {
        URL.revokeObjectURL(fileUrlEdit);
      }
    };
  }, [fileUrl, fileUrlEdit]);

  //
  return {
    // States
    cursos,
    selectedCursoNome,
    setSelectedCursoNome,
    selectedCursoId,
    setSelectedCursoId,
    turmas,
    selectedSemestreAno,
    setSelectedSemestreAno,
    selectedTurmaId,
    setSelectedTurmaId,
    selectedTurmaNome,
    setSelectedTurmaNome,
    disciplinas,
    selectedDisciplinaNome,
    setSelectedDisciplinaNome,
    selectedDisciplinaId,
    setSelectedDisciplinaId,
    mostrarAlunosSelecionados,
    setMostrarAlunosSelecionados,
    mostrarAlunos,
    setMostrarAlunos,
    alunos,
    setAlunos,
    alunosBk,
    setAlunosBk,
    selecionarAluno,
    setSelecionarAluno,
    steps,
    setSteps,
    file,
    setFile,
    fileUrl,
    setFileUrl,
    fileUrlEdit,
    setFileUrlEdit,
    uploading,
    loading,
    loading2,
    loading3,
    dragActive,
    setDragActive,
    searchRm,
    setSearchRm,
    searchNome,
    setSearchNome,
    errorText,
    fileSize,
    alunoLoading,
    complementosProg,
    setComplementosProg,
    dataInicio,
    setDataInicio,
    dataFim,
    setDataFim,
    isEditMode,
    filteredAlunos,
    valueInputDateInicio,
    valueInputDateFim,
    minimoDateFim,
    semAlunos,
    naoComecou,
    mostrarPdf,
    setMostrarPdf,
    setRemoveuFile,

    // Functions
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDataInicioChange,
    handleDataFimChange,
    handleUploadClick,
    handleEditSave,
    handleFechar,
    handleSelecionarAluno,
    handleContinuar,
    handleListarTurmas,
    handleGetDisciplina,
    handleListarAlunos,
    saveProgressaoTemporaria,
    fetchProgressaoTemporaria,
    DeleteProgreTemp,
  };
};

export default useProgressaoForm;
